import React, { useEffect, useState } from "react";
import {
    Box, Card, CardContent, Typography, Button, IconButton,
    Tooltip, TextField, Select, MenuItem, FormControl, InputLabel,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Snackbar, Alert, Chip, Divider, Skeleton,
} from "@mui/material";
import AddRoundedIcon          from "@mui/icons-material/AddRounded";
import EditRoundedIcon         from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon       from "@mui/icons-material/DeleteRounded";
import ArrowBackRoundedIcon    from "@mui/icons-material/ArrowBackRounded";
import InventoryRoundedIcon    from "@mui/icons-material/Inventory2Rounded";
import ScaleRoundedIcon        from "@mui/icons-material/ScaleRounded";
import CurrencyRupeeRoundedIcon from "@mui/icons-material/CurrencyRupeeRounded";
import SpeedRoundedIcon        from "@mui/icons-material/SpeedRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useNavigate }         from "react-router-dom";
import { getData, postData, API_ENDPOINTS } from "../../services/apiService.js";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
    teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
    orange:  "#FF7900", orangeLt:"#FFF0E0",
    indigo:  "#4F46E5", indigoLt:"#EEF2FF",
    amber:   "#D97706", amberLt: "#FEF3C7",
    emerald: "#059669", emeraldLt:"#D1FAE5",
    rose:    "#E11D48", roseLt:  "#FFE4E6",
    bg:      "#F7FAFC", border:  "#E2E8F0",
    text:    "#0F172A", muted:   "#64748B",
};

// ── KPI tile ──────────────────────────────────────────────────────────────
const KpiTile = ({ label, value, icon, iconBg, iconColor, accent }) => (
    <Card sx={{ borderRadius:"16px",border:`1px solid ${T.border}`,boxShadow:"0 1px 4px rgba(15,23,42,0.06)",overflow:"hidden",flex:"1 1 0" }}>
        <Box sx={{ height:3,background:`linear-gradient(90deg,${accent},${accent}66)` }} />
        <CardContent sx={{ p:2,"&:last-child":{ pb:2 },display:"flex",alignItems:"center",gap:1.5 }}>
            <Box sx={{ width:44,height:44,borderRadius:"12px",bgcolor:iconBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                {React.cloneElement(icon, { sx:{ fontSize:22,color:iconColor } })}
            </Box>
            <Box>
                <Typography sx={{ fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.3px" }}>{label}</Typography>
                <Typography sx={{ fontSize:20,fontWeight:900,color:T.text,fontFamily:"'Plus Jakarta Sans',sans-serif",mt:0.2 }}>{value}</Typography>
            </Box>
        </CardContent>
    </Card>
);

// ── Stat box ──────────────────────────────────────────────────────────────
const StatBox = ({ label, value, accent = T.teal }) => (
    <Box sx={{ flex:"1 1 140px",bgcolor:T.bg,borderRadius:"12px",border:`1px solid ${T.border}`,p:1.5 }}>
        <Typography sx={{ fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.3px" }}>{label}</Typography>
        <Typography sx={{ fontSize:16,fontWeight:800,color:accent,fontFamily:"'Plus Jakarta Sans',sans-serif",mt:0.3 }}>{value}</Typography>
    </Box>
);

// ── Main component ────────────────────────────────────────────────────────
export default function Package() {
    const navigate = useNavigate();
    const [packages,    setPackages]    = useState([]);
    const [openDialog,  setOpenDialog]  = useState(false);
    const [loading,     setLoading]     = useState(true);
    const [saving,      setSaving]      = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteId,    setDeleteId]    = useState(null);
    const [editMode,    setEditMode]    = useState(false);
    const [selectedPkg, setSelectedPkg] = useState(null);

    const [form, setForm] = useState({
        packageType:"", minWeight:"", maxWeight:"", basePrice:"",
        perMileRate:"", perKgRate:"", handlingFee:"", status:"", description:"",
    });

    const [snack, setSnack] = useState({ open:false, msg:"", sev:"success" });
    const toast = (msg, sev="success") => setSnack({ open:true, msg, sev });

    const fetchPackages = async () => {
        try {
            setLoading(true);
            const res = await getData(API_ENDPOINTS.getPackages);
            if (res?.statusCode === 200) setPackages(res.result.packageSizes || []);
        } catch(e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchPackages(); }, []);

    const resetForm = () => {
        setForm({ packageType:"", minWeight:"", maxWeight:"", basePrice:"",
            perMileRate:"", perKgRate:"", handlingFee:"", status:"", description:"" });
        setSelectedPkg(null); setEditMode(false);
    };

    const handleOpenDialog = (pkg) => {
        if (pkg) {
            setEditMode(true); setSelectedPkg(pkg);
            setForm({
                packageType: pkg.packageType,
                minWeight:   pkg.minWeight,
                maxWeight:   pkg.maxWeight,
                basePrice:   Number(pkg.basePrice),
                perMileRate: Number(pkg.perMileRate),
                perKgRate:   pkg.per_kg_rate != null ? Number(pkg.per_kg_rate) : "",
                handlingFee: Number(pkg.handlingFee),
                status:      pkg.status,
                description: pkg.description,
            });
        } else {
            resetForm();
        }
        setOpenDialog(true);
    };

    const handleSave = async () => {
        setSaving(true);
        const payload = {
            id:          selectedPkg?.id,
            packageType: form.packageType,
            minWeight:   Number(form.minWeight),
            maxWeight:   Number(form.maxWeight),
            basePrice:   Number(form.basePrice),
            perMileRate: Number(form.perMileRate),
            per_kg_rate: form.perKgRate !== "" ? Number(form.perKgRate) : null,
            handlingFee: Number(form.handlingFee),
            status:      form.status.toLowerCase(),
            description: form.description,
        };
        try {
            const res = editMode
                ? await postData(API_ENDPOINTS.updatePackages, payload)
                : await postData(API_ENDPOINTS.createPackages, payload);
            if (res?.statusCode === 200) {
                await fetchPackages(); resetForm(); setOpenDialog(false);
                toast(res.message || "Package saved successfully!");
            } else toast(res?.message || "Failed to save package!","error");
        } catch(e) { console.error(e); toast("Something went wrong!","error"); }
        finally { setSaving(false); }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteId) return;
        try {
            const res = await postData(`${API_ENDPOINTS.deletePackages}?id=${deleteId}`, {});
            if (res?.statusCode === 200) {
                await fetchPackages(); setDeleteDialogOpen(false); setDeleteId(null);
                toast(res.message || "Package deleted!");
            } else toast(res?.message || "Failed to delete!","error");
        } catch(e) { console.error(e); toast("Something went wrong!","error"); }
    };

    const avgBasePrice = packages.length > 0
        ? `₹${(packages.reduce((s,p)=>s+Number(p.basePrice),0)/packages.length).toFixed(2)}` : "₹0";
    const maxWeight = packages.length > 0
        ? `${Math.max(...packages.map(p=>p.maxWeight))} kg` : "0 kg";

    const statusChip = (status) => (
        <Chip label={status.charAt(0).toUpperCase()+status.slice(1)} size="small"
            sx={{ fontWeight:700,fontSize:11,height:24,
                bgcolor: status==="active" ? T.emeraldLt : T.roseLt,
                color:   status==="active" ? T.emerald   : T.rose,
                border:`1px solid ${status==="active" ? T.emerald+"44" : T.rose+"44"}`,
            }} />
    );

    const fld = (key) => (e) => setForm(p => ({ ...p, [key]: e.target.value }));

    return (
        <Box sx={{ bgcolor:T.bg, minHeight:"100vh", pb:4 }}>

            {/* ── Hero ── */}
            <Box sx={{ background:`linear-gradient(135deg,${T.teal} 0%,${T.tealDk} 55%,#005559 100%)`,
                borderRadius:"20px", p:3, mb:3,
                display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:2,
                boxShadow:"0 4px 20px rgba(0,124,132,0.2)" }}>
                <Box sx={{ display:"flex",alignItems:"center",gap:2 }}>
                    <IconButton onClick={()=>navigate("/app/settings")}
                        sx={{ bgcolor:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.25)",borderRadius:"10px",color:"#fff","&:hover":{ bgcolor:"rgba(255,255,255,0.25)" } }}>
                        <ArrowBackRoundedIcon sx={{ fontSize:18 }} />
                    </IconButton>
                    <Box sx={{ width:44,height:44,borderRadius:"12px",bgcolor:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.25)",display:"flex",alignItems:"center",justifyContent:"center" }}>
                        <InventoryRoundedIcon sx={{ fontSize:24,color:"#fff" }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize:22,fontWeight:800,color:"#fff",fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                            Package Management System
                        </Typography>
                        <Typography sx={{ fontSize:13,color:"rgba(255,255,255,0.75)",mt:0.3 }}>
                            Define pricing for different package size categories
                        </Typography>
                    </Box>
                </Box>
                <Button variant="contained" disableElevation startIcon={<AddRoundedIcon />}
                    onClick={()=>handleOpenDialog(null)}
                    sx={{ borderRadius:"12px",textTransform:"none",fontWeight:700,
                        bgcolor:"rgba(255,255,255,0.2)",border:"1px solid rgba(255,255,255,0.4)",
                        "&:hover":{ bgcolor:"rgba(255,255,255,0.3)" } }}>
                    Add Package Size
                </Button>
            </Box>

            {/* ── KPI row ── */}
            <Box sx={{ display:"flex",gap:2,mb:3,flexWrap:"wrap" }}>
                <KpiTile label="Active Sizes"      value={loading ? "—" : packages.length}
                    icon={<InventoryRoundedIcon />} iconBg={T.tealLt} iconColor={T.teal} accent={T.teal} />
                <KpiTile label="Avg Base Price"    value={loading ? "—" : avgBasePrice}
                    icon={<CurrencyRupeeRoundedIcon />} iconBg={T.indigoLt} iconColor={T.indigo} accent={T.indigo} />
                <KpiTile label="Max Package Weight" value={loading ? "—" : maxWeight}
                    icon={<ScaleRoundedIcon />} iconBg={T.amberLt} iconColor={T.amber} accent={T.amber} />
            </Box>

            {/* ── Package list card ── */}
            <Card sx={{ borderRadius:"16px",border:`1px solid ${T.border}`,boxShadow:"0 1px 4px rgba(15,23,42,0.06)",overflow:"hidden" }}>
                <Box sx={{ height:3,background:`linear-gradient(90deg,${T.teal},${T.tealLt})` }} />
                <CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 } }}>
                    <Box sx={{ display:"flex",justifyContent:"space-between",alignItems:"center",mb:2 }}>
                        <Box>
                            <Typography sx={{ fontSize:15,fontWeight:800,color:T.text,fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                                Package Size Categories
                            </Typography>
                            <Typography sx={{ fontSize:12,color:T.muted,mt:0.2 }}>
                                Configure pricing tiers for each weight band
                            </Typography>
                        </Box>
                        <Button variant="contained" disableElevation size="small"
                            startIcon={<AddRoundedIcon sx={{ fontSize:15 }} />}
                            onClick={()=>handleOpenDialog(null)}
                            sx={{ borderRadius:"10px",textTransform:"none",fontWeight:700,bgcolor:T.teal,"&:hover":{ bgcolor:T.tealDk } }}>
                            Add Package Size
                        </Button>
                    </Box>

                    {loading ? (
                        Array.from({length:3}).map((_,i) => (
                            <Box key={i} sx={{ mb:2 }}>
                                <Skeleton variant="rounded" height={90} sx={{ borderRadius:"12px" }} />
                            </Box>
                        ))
                    ) : packages.length === 0 ? (
                        <Box sx={{ textAlign:"center",py:6 }}>
                            <InventoryRoundedIcon sx={{ fontSize:48,color:T.border,mb:1 }} />
                            <Typography sx={{ color:T.muted,fontSize:14 }}>No package sizes defined yet</Typography>
                        </Box>
                    ) : (
                        packages.map((pkg, idx) => (
                            <Box key={pkg.id}>
                                <Box sx={{ py:2 }}>
                                    {/* Row header */}
                                    <Box sx={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",mb:1.5 }}>
                                        <Box>
                                            <Box sx={{ display:"flex",alignItems:"center",gap:1,mb:0.5 }}>
                                                <Typography sx={{ fontSize:16,fontWeight:800,color:T.text,fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                                                    {pkg.packageType}
                                                </Typography>
                                                {statusChip(pkg.status)}
                                            </Box>
                                            <Typography sx={{ fontSize:12,color:T.muted }}>
                                                {pkg.description || `${pkg.minWeight}–${pkg.maxWeight} kg range`}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display:"flex",alignItems:"center",gap:1,flexShrink:0 }}>
                                            <Typography sx={{ fontSize:11,color:T.muted }}>
                                                Created {new Date(pkg.createdAt).toLocaleDateString("en-IN")}
                                            </Typography>
                                            <Tooltip title="Edit">
                                                <IconButton size="small" onClick={()=>handleOpenDialog(pkg)}
                                                    sx={{ color:T.teal,bgcolor:T.tealLt,borderRadius:"8px","&:hover":{ bgcolor:"#b2e8ea" } }}>
                                                    <EditRoundedIcon sx={{ fontSize:15 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton size="small" onClick={()=>{ setDeleteId(pkg.id); setDeleteDialogOpen(true); }}
                                                    sx={{ color:T.rose,bgcolor:T.roseLt,borderRadius:"8px","&:hover":{ bgcolor:"#fecdd3" } }}>
                                                    <DeleteRoundedIcon sx={{ fontSize:15 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>

                                    {/* Stat boxes */}
                                    <Box sx={{ display:"flex",gap:1.5,flexWrap:"wrap" }}>
                                        <StatBox label="Min Weight" value={`${pkg.minWeight} kg`} />
                                        <StatBox label="Max Weight" value={`${pkg.maxWeight} kg`} />
                                        <StatBox label="Base Price" value={`₹${Number(pkg.basePrice).toFixed(2)}`} accent={T.indigo} />
                                        <StatBox label="Per km Rate" value={`₹${Number(pkg.perMileRate).toFixed(2)}/km`} accent={T.amber} />
                                        {pkg.handlingFee != null && Number(pkg.handlingFee) > 0 && (
                                            <StatBox label="Handling Fee" value={`₹${Number(pkg.handlingFee).toFixed(2)}`} accent={T.orange} />
                                        )}
                                        {pkg.per_kg_rate != null && (
                                            <StatBox label="Per kg Rate" value={`₹${Number(pkg.per_kg_rate).toFixed(2)}/kg`} accent={T.emerald} />
                                        )}
                                    </Box>
                                </Box>
                                {idx < packages.length - 1 && <Divider sx={{ borderColor:T.border }} />}
                            </Box>
                        ))
                    )}
                </CardContent>
            </Card>

            {/* ── Add / Edit Dialog ── */}
            <Dialog open={openDialog} onClose={()=>{ resetForm(); setOpenDialog(false); }} fullWidth maxWidth="sm"
                PaperProps={{ sx:{ borderRadius:"20px",overflow:"hidden" } }}>
                <Box sx={{ height:4,background:`linear-gradient(90deg,${T.teal},${T.tealLt})` }} />
                <DialogTitle sx={{ fontWeight:800,fontFamily:"'Plus Jakarta Sans',sans-serif",pb:1,display:"flex",alignItems:"center",gap:1.5 }}>
                    <Box sx={{ width:34,height:34,borderRadius:"9px",bgcolor:T.tealLt,display:"flex",alignItems:"center",justifyContent:"center" }}>
                        {editMode ? <EditRoundedIcon sx={{ fontSize:17,color:T.teal }} /> : <AddRoundedIcon sx={{ fontSize:17,color:T.teal }} />}
                    </Box>
                    {editMode ? "Edit Package Size" : "Add Package Size"}
                </DialogTitle>
                <DialogContent sx={{ pt:1 }}>
                    <Box sx={{ display:"flex",flexDirection:"column",gap:2,mt:1 }}>
                        <TextField label="Package Type" size="small" value={form.packageType} onChange={fld("packageType")}
                            InputProps={{ sx:{ borderRadius:"12px" } }} />
                        <Box sx={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:2 }}>
                            <TextField label="Min Weight (kg)" type="number" size="small" value={form.minWeight}
                                onChange={fld("minWeight")} InputProps={{ sx:{ borderRadius:"12px" } }} />
                            <TextField label="Max Weight (kg)" type="number" size="small" value={form.maxWeight}
                                onChange={fld("maxWeight")} InputProps={{ sx:{ borderRadius:"12px" } }} />
                            <TextField label="Base Price (₹)" type="number" size="small" value={form.basePrice}
                                onChange={fld("basePrice")} InputProps={{ sx:{ borderRadius:"12px" } }} />
                            <TextField label="Per Mile Rate (₹/km)" type="number" size="small" value={form.perMileRate}
                                onChange={fld("perMileRate")} InputProps={{ sx:{ borderRadius:"12px" } }} />
                            <TextField label="Per Kg Rate (₹/kg)" type="number" size="small" value={form.perKgRate}
                                onChange={fld("perKgRate")} helperText="Optional"
                                InputProps={{ sx:{ borderRadius:"12px" } }} />
                            <TextField label="Handling Fee (₹)" type="number" size="small" value={form.handlingFee}
                                onChange={fld("handlingFee")} InputProps={{ sx:{ borderRadius:"12px" } }} />
                        </Box>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select value={form.status} onChange={fld("status")} label="Status"
                                sx={{ borderRadius:"12px" }}>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField label="Description" size="small" multiline rows={3} value={form.description}
                            onChange={fld("description")} InputProps={{ sx:{ borderRadius:"12px" } }} />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px:3,pb:2.5,gap:1 }}>
                    <Button onClick={()=>{ resetForm(); setOpenDialog(false); }}
                        sx={{ borderRadius:"10px",textTransform:"none",fontWeight:600,color:T.muted,borderColor:T.border }}
                        variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleSave} variant="contained" disableElevation disabled={saving}
                        sx={{ borderRadius:"10px",textTransform:"none",fontWeight:700,bgcolor:T.teal,"&:hover":{ bgcolor:T.tealDk },minWidth:80 }}>
                        {saving ? "Saving…" : editMode ? "Update" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Delete Confirmation ── */}
            <Dialog open={deleteDialogOpen} onClose={()=>setDeleteDialogOpen(false)}
                PaperProps={{ sx:{ borderRadius:"20px",overflow:"hidden" } }}>
                <Box sx={{ height:4,background:`linear-gradient(90deg,${T.rose},${T.roseLt})` }} />
                <DialogTitle sx={{ fontWeight:800,fontFamily:"'Plus Jakarta Sans',sans-serif",display:"flex",alignItems:"center",gap:1.5 }}>
                    <Box sx={{ width:34,height:34,borderRadius:"9px",bgcolor:T.roseLt,display:"flex",alignItems:"center",justifyContent:"center" }}>
                        <WarningAmberRoundedIcon sx={{ fontSize:18,color:T.rose }} />
                    </Box>
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize:14,color:T.muted }}>
                        Are you sure you want to delete this package size? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px:3,pb:2.5,gap:1 }}>
                    <Button onClick={()=>setDeleteDialogOpen(false)} variant="outlined"
                        sx={{ borderRadius:"10px",textTransform:"none",fontWeight:600,borderColor:T.border,color:T.muted }}>
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} variant="contained" disableElevation color="error"
                        sx={{ borderRadius:"10px",textTransform:"none",fontWeight:700 }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snack.open} autoHideDuration={3500} onClose={()=>setSnack(s=>({...s,open:false}))}
                anchorOrigin={{ vertical:"top",horizontal:"center" }}>
                <Alert severity={snack.sev} variant="filled" onClose={()=>setSnack(s=>({...s,open:false}))}
                    sx={{ borderRadius:"12px" }}>{snack.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
